import { AugmentedI18n, BaseTranslationDictionary, Languages, TranslationValue } from "./types.js";

export const createProxy = (obj: object, keyPath = ""): any => {
    return new Proxy(obj, {
        get: (objParams, prop) => {
            const newKey = `${keyPath ? `${keyPath}.` : ""}${prop.toString()}`;
            try {
                const propData = Reflect.has(objParams, prop) ? Reflect.get(objParams, prop) : null;
                if (Boolean(propData) === true) {
                    return propData;
                }

                return createProxy(() => newKey);
            } catch (err) {
                return createProxy(() => newKey);
            }
        },
        set: (proxyObj, prop, val) => {
            Reflect.set(proxyObj, prop, val);
            return true;
        },
    });
};

export const walkObject = <TranslationDictionary extends BaseTranslationDictionary>(
    obj: object,
    objectFn: typeof createProxy,
    i18next: AugmentedI18n<TranslationDictionary>,
    keyPath = "",
) => {
    Object.keys(obj).forEach((key) => {
        const keyValue = Reflect.get(obj, key);
        if (typeof keyValue === "object") {
            walkObject(keyValue, objectFn, i18next, `${keyPath ? `${keyPath}.` : ""}${key}`);
        }
        Reflect.set(obj, key, objectFn(keyValue, `${keyPath ? `${keyPath}.` : ""}${key}`));
    });
};

export const convertToFunctions = <TranslationDictionary extends BaseTranslationDictionary>(obj: object, current = ""): TranslationDictionary => {
    const dict: TranslationValue = {};
    Object.keys(obj).forEach((key) => {
        const currentLookupKey = current ? `${current}.${key}` : key;
        const keyValue = Reflect.get(obj, key);
        if (typeof keyValue === "object") {
            dict[key] = convertToFunctions<TranslationDictionary>(keyValue, currentLookupKey);
        } else {
            dict[key] = () => currentLookupKey;
        }
    });

    return dict as TranslationDictionary;
};

export const buildTranslations = <TranslationDictionary extends BaseTranslationDictionary>(
    i18next: AugmentedI18n<TranslationDictionary>,
    language?: Languages,
): TranslationDictionary => {
    const currentLanguage = language || i18next.language || Languages.EN;

    if (i18next.translationsData[currentLanguage]) {
        return i18next.translationsData[currentLanguage];
    }
    const languageData = i18next.getDataByLanguage?.(currentLanguage);

    let translations: TranslationDictionary;

    if (languageData) {
        translations = convertToFunctions(languageData.translation);
        walkObject(translations, createProxy, i18next);
        translations = createProxy(translations);
        i18next.translationsData[currentLanguage] = translations;
    } else {
        translations = createProxy({});
    }
    return translations;
};
